import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DemandeEnAttente } from './useModificationsSensibles';
import { appelApi } from './api';
import type { EvaEnBref } from './useEva';

// ---------------------------------------------------------------------------
// Tests de positionnement (CDC §6.5)
//
// Le pôle pédagogique compose les listes de questions par niveau, attribue le
// test de la formation suivie, le fait passer sur tablette ou l'apprenant le
// passe dans son espace. Les règles — barème, cohérence des questions, notes —
// sont tenues par l'API ; l'écran ne fait qu'éviter un envoi évidemment vide.
// ---------------------------------------------------------------------------

export type TypeQuestion = 'CHOIX_UNIQUE' | 'CHOIX_MULTIPLE' | 'REPONSE_LIBRE';
export type EtatTest = 'A_PASSER' | 'A_CORRIGER' | 'CORRIGE';
export type ModePassageTest = 'TABLETTE' | 'ESPACE';

export const LIBELLES_TYPE_QUESTION: Record<TypeQuestion, string> = {
  CHOIX_UNIQUE: 'Choix unique',
  CHOIX_MULTIPLE: 'Choix multiple',
  REPONSE_LIBRE: 'Réponse libre',
};

export interface QuestionTest {
  id?: string;
  enonce: string;
  type: TypeQuestion;
  points: number;
  choix: { id?: string; libelle: string; correct: boolean }[];
  attendu: string | null;
}

/** Question telle que l'apprenant la voit : ni bonne réponse, ni éléments attendus. */
export interface QuestionPosee {
  id: string;
  enonce: string;
  type: TypeQuestion;
  points: number;
  choix: { id: string; libelle: string }[];
}

export type Reponses = Record<string, { choix?: string[]; texte?: string }>;

export interface CorrectionQuestion {
  points: number | null;
  source: 'AUTO' | 'IA' | 'MANUEL';
  commentaire: string | null;
  proposition: { points: number; justification: string } | null;
}

export interface Correcteur {
  actif: boolean;
  nom: string | null;
}

export interface LigneTest {
  id: string;
  apprenantId: string;
  apprenant: string;
  siteCode: string;
  formation: string | null;
  formationCode: string | null;
  niveauFormation: string | null;
  titre: string | null;
  score: number | null;
  scoreSur: number;
  niveau: string | null;
  corrigeLe: string | null;
  corrigePar: string | null;
  soumisLe: string | null;
  modePassage: ModePassageTest | null;
  passeLe: string;
  etat: EtatTest;
  etatLibelle: string;
  etatTon: string;
  aNoter: number;
  /** Résultat EVA qui fait foi pour ce dossier (TF-441), lu à côté du test. */
  eva: EvaEnBref | null;
}

export interface ApprenantTestable {
  id: string;
  nom: string;
  siteCode: string;
  formation: string;
  niveau: string;
}

export interface ReponseTests {
  tests: LigneTest[];
  total: number;
  compteurs: {
    aPasser: number;
    aCorriger: number;
    corriges: number;
    scoreMoyen: number | null;
  };
  niveaux: { niveau: string; formations: string[] }[];
  apprenants: ApprenantTestable[];
  correcteur: Correcteur;
}

export interface ModeleTest {
  id: string;
  titre: string;
  niveau: string;
  consigne: string | null;
  actif: boolean;
  questions: (QuestionTest & { id: string })[];
  passages: number;
  modifieLe: string;
}

export interface EntreeModeleTest {
  titre: string;
  niveau: string;
  consigne?: string;
  actif: boolean;
  questions: QuestionTest[];
}

export interface Passation {
  id: string;
  titre: string;
  apprenant: string;
  consigne: string | null;
  questions: QuestionPosee[];
  soumisLe: string | null;
}

export interface ResultatRemise {
  message: string;
  etat: EtatTest;
  score: number | null;
}

export interface CopieTest {
  id: string;
  titre: string;
  apprenant: string;
  niveau: string | null;
  questions: (QuestionTest & { id: string })[];
  reponses: Reponses;
  correction: Record<string, CorrectionQuestion>;
  score: number | null;
  commentaire: string | null;
  soumisLe: string | null;
  corrigeLe: string | null;
  corrigePar: string | null;
  modePassage: ModePassageTest | null;
  correcteur: Correcteur;
  /**
   * Corrigé, le test a sa note lue par l'apprenant : une nouvelle correction se
   * demande à la direction (TF-437). La demande qui attend sa décision.
   */
  demandeEnAttente: DemandeEnAttente | null;
}

export interface MonTest {
  id: string;
  titre: string;
  etat: EtatTest;
  etatLibelle: string;
  score: number | null;
  attribueLe: string;
  soumisLe: string | null;
}

function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

function invaliderTests(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['tests-positionnement'] });
  void client.invalidateQueries({ queryKey: ['modeles-tests'] });
  void client.invalidateQueries({ queryKey: ['mes-tests'] });
  void client.invalidateQueries({ queryKey: ['dashboard-pedagogique'] });
}

export function useTests(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['tests-positionnement', parametres],
    queryFn: () => appelApi<ReponseTests>(`/pedagogique/tests${parametres}`),
  });
}

export function useModelesTests() {
  return useQuery({
    queryKey: ['modeles-tests'],
    queryFn: () => appelApi<ModeleTest[]>('/pedagogique/tests/modeles'),
  });
}

/** Création, ou correction quand `id` est donné : les passages déjà attribués n'en bougent pas. */
export function useEnregistrerModeleTest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...entree }: EntreeModeleTest & { id?: string }) =>
      appelApi<ModeleTest>(id ? `/pedagogique/tests/modeles/${id}` : '/pedagogique/tests/modeles', {
        methode: id ? 'PUT' : 'POST',
        corps: entree,
      }),
    onSuccess: () => invaliderTests(client),
  });
}

export function useAttribuerTest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { apprenantId: string; modeleId: string }) =>
      appelApi<{ id: string; message: string }>('/pedagogique/tests/attributions', {
        methode: 'POST',
        corps: variables,
      }),
    onSuccess: () => invaliderTests(client),
  });
}

/** Passation sur la tablette du centre, ouverte par le pôle pédagogique. */
export function usePassationTablette(testId: string | null) {
  return useQuery({
    queryKey: ['passation-test', testId],
    queryFn: () => appelApi<Passation>(`/pedagogique/tests/${testId ?? ''}/passation`),
    enabled: Boolean(testId),
  });
}

export function useRemettreTablette() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { testId: string; reponses: Reponses }) =>
      appelApi<ResultatRemise>(`/pedagogique/tests/${variables.testId}/reponses`, {
        methode: 'POST',
        corps: { reponses: variables.reponses },
      }),
    onSuccess: () => invaliderTests(client),
  });
}

export function useCopieTest(testId: string | null) {
  return useQuery({
    queryKey: ['copie-test', testId],
    queryFn: () => appelApi<CopieTest>(`/pedagogique/tests/${testId ?? ''}/copie`),
    enabled: Boolean(testId),
  });
}

export function useCorrigerTest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      testId: string;
      notes: Record<string, { points: number; commentaire?: string }>;
      commentaire?: string;
    }) =>
      appelApi<CopieTest>(`/pedagogique/tests/${variables.testId}/correction`, {
        methode: 'PUT',
        corps: { notes: variables.notes, commentaire: variables.commentaire },
      }),
    onSuccess: (copie) => {
      invaliderTests(client);
      client.setQueryData(['copie-test', copie.id], copie);
    },
  });
}

// --- Espace apprenant ------------------------------------------------------

export function useMesTests() {
  return useQuery({
    queryKey: ['mes-tests'],
    queryFn: () => appelApi<MonTest[]>('/espace-apprenant/tests'),
  });
}

export function usePassationEspace(testId: string | null) {
  return useQuery({
    queryKey: ['passation-espace', testId],
    queryFn: () => appelApi<Passation>(`/espace-apprenant/tests/${testId ?? ''}`),
    enabled: Boolean(testId),
  });
}

export function useRemettreEspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { testId: string; reponses: Reponses }) =>
      appelApi<ResultatRemise>(`/espace-apprenant/tests/${variables.testId}/reponses`, {
        methode: 'POST',
        corps: { reponses: variables.reponses },
      }),
    onSuccess: () => invaliderTests(client),
  });
}
